/**
 * DeductionsSection Component
 * Displays project-level deductions (refunds + late penalty interest) and
 * exposes add / edit / delete. Deductions reduce the remaining balance and
 * render as red segments on the payment progress bar.
 */

import React, { useState } from 'react';
import { formatCurrency } from '../../utils/formatters';
import { paymentDeductionAPI } from '../../services/api';
import { toast } from 'react-toastify';
import AddDeductionModal from './AddDeductionModal';
import EditDeductionModal from './EditDeductionModal';

const TYPE_LABELS = {
  refund: 'Refund',
  late_penalty_interest: 'Late Penalty Interest'
};

const TYPE_BADGE = {
  refund: 'bg-red-100 text-red-700',
  late_penalty_interest: 'bg-orange-100 text-orange-700'
};

function formatDate(d) {
  if (!d) return '-';
  return new Date(d).toLocaleDateString('en-MY', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
}

const DeductionsSection = ({ projectId, summary, onRefresh }) => {
  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editing, setEditing] = useState(null);

  const deductions = summary?.deductions || [];
  const totalDeductions = summary?.totalDeductions || 0;
  const byType = summary?.deductionsByType || { refund: 0, late_penalty_interest: 0 };

  const handleDelete = async (deduction) => {
    if (deduction.sourceVariationOrderId) {
      toast.error('This deduction was created automatically when a VO was voided and cannot be deleted separately');
      return;
    }
    if (!window.confirm('Delete this deduction? This will restore the amount to the remaining balance.')) {
      return;
    }
    try {
      await paymentDeductionAPI.delete(projectId, deduction.id);
      toast.success('Deduction deleted');
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete deduction');
    }
  };

  const handleEdit = (deduction) => {
    setEditing(deduction);
    setShowEdit(true);
  };

  return (
    <div className="border-t border-gray-200 pt-4 mt-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <h4 className="text-sm font-semibold text-gray-900">Deductions</h4>
          {deductions.length > 0 && (
            <span className="bg-red-100 text-red-700 text-xs font-medium px-2 py-0.5 rounded-full">
              {deductions.length}
            </span>
          )}
        </div>
        <button
          onClick={() => setShowAdd(true)}
          className="flex items-center gap-1 text-sm text-red-600 hover:text-red-700 font-medium min-h-[44px] px-2 touch-manipulation"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Deduction
        </button>
      </div>

      {deductions.length > 0 && (
        <div className="grid grid-cols-3 gap-2 mb-4">
          <div className="bg-red-50 rounded-lg p-2 text-center">
            <p className="text-xs text-gray-500">Total</p>
            <p className="font-semibold text-red-700 text-sm">{formatCurrency(totalDeductions)}</p>
          </div>
          <div className="bg-red-50 rounded-lg p-2 text-center">
            <p className="text-xs text-gray-500">Refunds</p>
            <p className="font-semibold text-red-700 text-sm">{formatCurrency(byType.refund || 0)}</p>
          </div>
          <div className="bg-orange-50 rounded-lg p-2 text-center">
            <p className="text-xs text-gray-500">Penalty</p>
            <p className="font-semibold text-orange-700 text-sm">{formatCurrency(byType.late_penalty_interest || 0)}</p>
          </div>
        </div>
      )}

      {deductions.length === 0 ? (
        <div className="text-center py-6 bg-gray-50 rounded-lg">
          <p className="text-sm text-gray-500">No deductions recorded</p>
          <button
            onClick={() => setShowAdd(true)}
            className="mt-2 text-sm text-red-600 hover:text-red-700 font-medium"
          >
            Record a refund or late penalty
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {deductions.map(d => (
            <div key={d.id} className="border border-gray-200 rounded-lg p-3 bg-white">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${TYPE_BADGE[d.type] || 'bg-gray-100 text-gray-700'}`}>
                      {TYPE_LABELS[d.type] || d.type}
                    </span>
                    <span className="text-xs text-gray-500">{formatDate(d.deductionDate)}</span>
                    {d.referenceNumber && (
                      <span className="text-xs text-gray-400">Ref: {d.referenceNumber}</span>
                    )}
                    {d.sourceVariationOrderId && (
                      <span className="text-xs text-gray-400 italic">(auto-created from VO void)</span>
                    )}
                  </div>
                  {d.reason && (
                    <p className="text-sm text-gray-600 mt-1">{d.reason}</p>
                  )}
                  {d.recorder && (
                    <p className="text-xs text-gray-400 mt-1">
                      Recorded by {d.recorder.nickname || d.recorder.fullName}
                    </p>
                  )}
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-sm font-semibold text-red-600">
                    −{formatCurrency(d.amount)}
                  </p>
                  <div className="flex gap-2 justify-end mt-1">
                    {!d.sourceVariationOrderId && (
                      <button
                        onClick={() => handleEdit(d)}
                        className="text-xs text-blue-600 hover:text-blue-700"
                      >
                        Edit
                      </button>
                    )}
                    {!d.sourceVariationOrderId && (
                      <button
                        onClick={() => handleDelete(d)}
                        className="text-xs text-red-600 hover:text-red-700"
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <AddDeductionModal
        isOpen={showAdd}
        onClose={() => setShowAdd(false)}
        onSuccess={() => {
          setShowAdd(false);
          onRefresh();
        }}
        projectId={projectId}
      />

      <EditDeductionModal
        isOpen={showEdit}
        onClose={() => {
          setShowEdit(false);
          setEditing(null);
        }}
        onSuccess={() => {
          setShowEdit(false);
          setEditing(null);
          onRefresh();
        }}
        projectId={projectId}
        deduction={editing}
      />
    </div>
  );
};

export default DeductionsSection;
